import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AboutMetricsService } from '../../services/about-metrics.service';
import {
  AboutMetricsResponse,
  ABOUT_METRICS_FALLBACK,
  ABOUT_METRIC_KEYS,
} from '../../models/about-metrics.model';

/**
 * Redesigned About page (approved prototype "About — PlateX 7"). Served at
 * /about-us on dev environments (dev2 host + local dev); production continues to
 * serve the existing AboutUsPageComponent (see app.module). It renders inside
 * the existing app shell, so the shared nav-shell header and footer are provided
 * by AppComponent — this component renders page content sections only.
 *
 * All headline metrics are bound from the cached /api/stats/about-metrics
 * endpoint via AboutMetricsService. The page is seeded synchronously with the
 * single central ABOUT_METRICS_FALLBACK so it never renders zeroes or empty
 * cards while the request is in flight, then overwritten when the API emits
 * (live or server-cached). No metric values are hardcoded here or in the
 * template — they all originate from the service or that one central fallback.
 */
@Component({
  selector: 'pla-about-us-page-v2',
  templateUrl: './about-us-page-v2.component.html',
  styleUrls: ['./about-us-page-v2.component.scss'],
})
export class AboutUsPageV2Component implements OnInit, OnDestroy {
  public metrics: AboutMetricsResponse = ABOUT_METRICS_FALLBACK;
  public readonly metricKeys = ABOUT_METRIC_KEYS;

  // CTA targets — reuse existing PlateX routes.
  public readonly searchRoute = '/buy';            // main search / landing entry
  public readonly dealerRoute = '/dealer/home';    // dealer portal
  public readonly contactHref = 'mailto:info@platex.co.uk'; // matches footer "Contact us"
  public readonly founderHref = 'mailto:hugh@platex.co.uk';

  private sub?: Subscription;

  constructor(private aboutMetricsService: AboutMetricsService) {}

  /** Numeric portion of the hero coverage figure (e.g. "79" from "79M+"). */
  public get heroValue(): string {
    return this.splitDisplay(this.metrics.searchableRegistrations.display).value;
  }

  /** Magnitude suffix of the hero figure (e.g. "M+"), shown in green. */
  public get heroSuffix(): string {
    return this.splitDisplay(this.metrics.searchableRegistrations.display).suffix;
  }

  private splitDisplay(display: string): { value: string; suffix: string } {
    const d = (display || '').trim();
    const m = d.match(/^([\d.,]+)(.*)$/);
    return m ? { value: m[1], suffix: m[2] } : { value: d, suffix: '' };
  }

  ngOnInit(): void {
    this.sub = this.aboutMetricsService
      .getAboutMetrics()
      .subscribe((res) => (this.metrics = res));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
